import React, { useState, useEffect } from 'react';
import { X, Send, User, Mail, Briefcase, MapPin, MessageSquare, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { axiosInstance as axios } from '../../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const InviteProfessionalModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    professional_name: '',
    professional_email: '',
    specialty: '',
    city: '',
    region: '',
    country: 'USA',
    personal_message: ''
  });
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchSpecialties();
      // Reset form when modal opens
      setFormData({
        professional_name: '',
        professional_email: '',
        specialty: '',
        city: '',
        region: '',
        country: 'USA',
        personal_message: ''
      });
      setSuccess(false);
      setError('');
    }
  }, [isOpen]);

  const fetchSpecialties = async () => {
    try {
      const response = await axios.get(`${API}/professional-invites/specialties`);
      setSpecialties(response.data.specialties || []);
    } catch (err) {
      console.error('Error fetching specialties:', err);
      // Fallback specialties
      setSpecialties([
        { value: 'financial_planner', label: 'Financial Planner', phase: 1 },
        { value: 'estate_planner', label: 'Estate Planner', phase: 1 },
        { value: 'attorney', label: 'Attorney', phase: 1 },
        { value: 'genealogist', label: 'Genealogist', phase: 2 },
        { value: 'family_counselor', label: 'Family Counselor', phase: 2 },
        { value: 'mediator', label: 'Mediator', phase: 2 }
      ]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/professional-invites`, formData, {
        withCredentials: true
      });
      
      setSuccess(true);
      
      // Auto close after success
      setTimeout(() => {
        onSuccess && onSuccess(response.data);
        onClose();
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="invite-professional-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Invite a Professional</h2>
            <p className="text-blue-100 text-sm">Help grow our partner network</p>
          </div>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
            data-testid="close-invite-modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Success State */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Invitation Sent!</h3>
            <p className="text-gray-600">
              We've sent an invitation email to <strong>{formData.professional_name}</strong>. 
              You'll be notified when they sign up!
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Error Alert */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={18} />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Professional Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Professional's Name *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  name="professional_name"
                  value={formData.professional_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., John Smith, CPA"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="professional-name-input"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="email"
                  name="professional_email"
                  value={formData.professional_email}
                  onChange={handleChange}
                  required
                  placeholder="john@example.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="professional-email-input"
                />
              </div>
            </div>

            {/* Specialty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specialty *
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <select
                  name="specialty"
                  value={formData.specialty}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
                  data-testid="specialty-select"
                >
                  <option value="">Select a specialty...</option>
                  <optgroup label="Phase 1 - Priority">
                    {specialties.filter(s => s.phase === 1).map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Phase 2 - Coming Soon">
                    {specialties.filter(s => s.phase === 2).map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
            </div>

            {/* Location Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    placeholder="Atlanta"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="city-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State/Region
                </label>
                <input
                  type="text"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  placeholder="GA"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="region-input"
                />
              </div>
            </div>

            {/* Personal Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personal Message (Optional)
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 text-gray-400" size={18} />
                <textarea
                  name="personal_message"
                  value={formData.personal_message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Add a personal note to your invitation..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  data-testid="personal-message-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">This will be included in the invitation email</p>
            </div>

            {/* Referral Rewards Info */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <span className="text-lg">🎁</span> Referral Rewards
              </h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• <strong>1st referral:</strong> 20% off next month</li>
                <li>• <strong>3 referrals:</strong> 1 month free</li>
                <li>• <strong>5+ referrals:</strong> Community Builder badge + 10% permanent discount</li>
              </ul>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition"
                data-testid="cancel-invite-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-medium transition flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="send-invite-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Invitation
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InviteProfessionalModal;
